import React, { useEffect, useRef, useState } from 'react'
import { QRCodeCanvas } from 'qrcode.react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import BasicButton, { BASIC_BUTTON_TYPE_OUTLINED } from '@divvy-web/skylab.basicbutton'
import Dialog, { DialogHeader } from '@divvy-web/skylab.dialog'
import Spinner from '@divvy-web/skylab.spinner'

import { useAuth } from '@sputnik/auth'
import { PATHNAME_CONSTS, PRODUCT_CONST_STRINGS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'
import { logError } from '@sputnik/utils/loggerUtils'

import { useGetTokenForMobileQRCode } from './GetTokenForMobileQRCode.gql'
import styles from './styles.module.scss'

const QR_EXPIRY_MS = 15 * 60 * 1000

export interface MobileUploadQRSectionProps {
  salesforceCreditId: string
}

const MobileUploadQRSection = ({ salesforceCreditId }: MobileUploadQRSectionProps) => {
  const [getClassName, makeTestId] = useNameFormatter('MobileUploadQRSection')
  const { email } = useAuth()
  const [getTokenForMobileQrCode, { loading: isGettingTokenForMobileQrCode }] = useGetTokenForMobileQRCode()
  const [isShowing, setIsShowing] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  const [isExpired, setIsExpired] = useState(false)
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hostName = window.location.hostname

  const { track } = useWebTracking()

  const clearExpiryTimer = () => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current)
      expiryTimerRef.current = null
    }
  }

  useEffect(() => clearExpiryTimer, [])

  const fetchTokenAndOpen = async () => {
    track('se_signup__upload_from_your_phone__fired')
    setIsExpired(false)
    setToken(null)
    setIsShowing(true)

    try {
      const { data } = await getTokenForMobileQrCode({ variables: { salesforceCreditId } })
      setToken(data?.requestApplicantToken?.token ?? '')

      clearExpiryTimer()
      expiryTimerRef.current = setTimeout(() => setIsExpired(true), QR_EXPIRY_MS)
    } catch (e) {
      logError(e, {
        attributes: {
          action: 'requestMobileDocumentUploadToken',
          creditAppId: salesforceCreditId,
          result: e,
        },
        eventName: 'UploadDocument',
      })
    }
  }

  const handleClose = () => {
    setIsShowing(false)
    clearExpiryTimer()
  }

  return (
    <>
      <BasicButton
        className={styles[getClassName('upload-from-your-phone')]}
        dataTestId={makeTestId('upload-from-your-phone')}
        type={BASIC_BUTTON_TYPE_OUTLINED}
        onClick={fetchTokenAndOpen}
      >
        <FormattedMessage
          defaultMessage='Upload from your phone'
          id='sputnik.MobileUploadQRSection__HKt3aZ'
        />
      </BasicButton>
      <Dialog
        dataTestId={makeTestId('')}
        isShowing={isShowing}
        onCloseClick={handleClose}
      >
        <DialogHeader
          title={
            <FormattedMessage
              defaultMessage='Upload from your phone'
              id='sputnik.MobileUploadQRSection__HKt3aZ'
            />
          }
        />
        {isExpired && (
          <div
            className={styles[getClassName('expired')]}
            data-testid={makeTestId('expired')}
          >
            <div className={styles[getClassName('expired-message')]}>
              <FormattedMessage
                defaultMessage='This QR code has expired.'
                id='sputnik.MobileUploadQRSection__FQCW4g'
              />
            </div>
            <BasicButton
              dataTestId={makeTestId('refresh')}
              onClick={fetchTokenAndOpen}
            >
              <FormattedMessage
                defaultMessage='Generate new QR code'
                id='sputnik.MobileUploadQRSection__nRp2iD'
              />
            </BasicButton>
          </div>
        )}
        {!isExpired && (
          <div className={styles[getClassName('container')]}>
            <div className={styles[getClassName('instructions')]}>
              <FormattedMessage
                defaultMessage="Scan this QR code with your phone's camera to upload documents."
                id='sputnik.MobileUploadQRSection__CWac43'
              />
            </div>
            <div
              className={styles[getClassName('qr-wrapper')]}
              data-testid={makeTestId('qr-wrapper')}
            >
              {/* COM-10698: split the following code into their own components */}
              {isGettingTokenForMobileQrCode || !token ? (
                <Spinner centered />
              ) : (
                <QRCodeCanvas
                  imageSettings={{
                    height: 35,
                    src: 'https://app.divvy.co/assets/logos/document-upload-type-modal-qrcode@3x.png',
                    width: 35,
                    excavate: false,
                  }}
                  size={160}
                  value={`https://${hostName}/verify-email?referrer=${PATHNAME_CONSTS.DASHBOARD_PATH}/${salesforceCreditId}/${PATHNAME_CONSTS.DASHBOARD_PATH_DOCUMENTS_DRAWER}&token=${token}&email=${email}&source=mobile_qr`}
                />
              )}
            </div>
            <div className={styles[getClassName('caption')]}>
              <FormattedMessage
                defaultMessage='Opens a secure {BILL} upload page—no app needed'
                id='sputnik.MobileUploadQRSection__KPN9fu'
                values={{ BILL: PRODUCT_CONST_STRINGS.BILL }}
              />
            </div>
          </div>
        )}
      </Dialog>
    </>
  )
}

export default MobileUploadQRSection
